import { ClinicProduct, ListResult, PublicProductQuery } from '../types';
import { db, findProductById } from './db';
import { delay } from './delay';
import { ERR_NOT_FOUND } from './errors';

const isPurchasable = (p: ClinicProduct): boolean => {
  if (p.productType === 'VIRTUAL') {
    const vc = p.virtualConfig;
    if (vc?.autoDelistAt && Date.parse(vc.autoDelistAt) <= Date.now()) return false;
    if (vc && vc.capacity !== null && vc.capacity - vc.reservedSlots - vc.soldSlots <= 0) return false;
    return true;
  }
  return p.stockStatus !== 'OUT_OF_STOCK';
};

const matches = (p: ClinicProduct, q: PublicProductQuery): boolean => {
  if (q.category && p.category !== q.category) return false;
  if (q.productType && p.productType !== q.productType) return false;
  if (q.supplierSlug && p.supplier?.publicSlug !== q.supplierSlug) return false;
  if (q.keyword) {
    const k = q.keyword.trim().toLowerCase();
    if (k.length > 0) {
      const inName = p.nameEn.toLowerCase().includes(k);
      const inBrand = (p.brand ?? '').toLowerCase().includes(k);
      if (!inName && !inBrand) return false;
    }
  }
  return true;
};

export const publicCatalogMock = {
  list: async (
    query: PublicProductQuery = {},
  ): Promise<ListResult<ClinicProduct>> => {
    await delay();
    const skip = query.skip ?? 0;
    const take = query.take ?? 0;
    const filtered = db.products.filter((p) => matches(p, query) && isPurchasable(p));
    const items = take === 0 ? filtered.slice(skip) : filtered.slice(skip, skip + take);
    return { items, skip, take, total: filtered.length };
  },

  detail: async (productId: string): Promise<ClinicProduct> => {
    await delay();
    const p = findProductById(productId);
    if (!p) throw ERR_NOT_FOUND('Product');
    return { ...p };
  },

  facets: async () => {
    await delay();
    const purchasable = db.products.filter(isPurchasable);
    const brandCounts = new Map<string, number>();
    let min = Infinity;
    let max = 0;
    for (const p of purchasable) {
      if (p.brand) brandCounts.set(p.brand, (brandCounts.get(p.brand) ?? 0) + 1);
      if (p.salePrice < min) min = p.salePrice;
      if (p.salePrice > max) max = p.salePrice;
    }
    const brands = Array.from(brandCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
    return {
      brands,
      priceMin: Math.floor(min === Infinity ? 0 : min),
      priceMax: Math.ceil(max),
      items: purchasable.map((p) => ({ ...p })),
    };
  },
};
